import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CodeUniteTemps} from './enum/code-unite-temps.enum';
import {UniteTempsAccesRapide} from './enum/unite-temps-acces-rapide.enum';
import {UniteTempsEnum} from './enum/unite-temps.enum';
import {ChoixAccesRapide} from './model/choix-acces-rapide.model';
import {UniteTemps} from './model/unite-temps.model';
import {InputMaskModule} from 'primeng/inputmask';
import {OverlayModule} from 'primeng/overlay';
import {TabViewModule} from 'primeng/tabview';
import {TranslateModule} from '@ngx-translate/core';
import {InputNumberModule} from 'primeng/inputnumber';
import {CalendarModule} from 'primeng/calendar';
import {DropdownModule} from 'primeng/dropdown';
import {DividerModule} from 'primeng/divider';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RadioButtonModule} from 'primeng/radiobutton';
import {CommonModule} from '@angular/common';
import { AbstractSigaleComponent } from '../abstract-sigale.component';

@Component({
	selector: 'sigale-input-date',
	templateUrl: './input-date.component.html',
	styleUrls: ['./input-date.component.scss'],
	imports: [
		InputMaskModule,
		OverlayModule,
		TabViewModule,
		TranslateModule,
		InputNumberModule,
		CalendarModule,
		DropdownModule,
		DividerModule,
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		RadioButtonModule
	],
	standalone: true
})
export class InputDateComponent extends AbstractSigaleComponent implements OnInit {

	@Input() public dateDebut: Date;
	@Input() public dateFin: Date;
	@Output() public dateDebutChange: EventEmitter<Date> = new EventEmitter<Date>();
	@Output() public dateFinChange: EventEmitter<Date> = new EventEmitter<Date>();

	public selecteurDateOuvert = false;
	public duree: number;
	public unitesTemps: UniteTemps[] = UniteTempsEnum;
	public accesRapides: ChoixAccesRapide[] = UniteTempsAccesRapide;
	public accesRapidesMinutes: ChoixAccesRapide[];
	public accesRapidesHeures: ChoixAccesRapide[];
	public accesRapidesJours: ChoixAccesRapide[];
	public accesRapideSelectionne: ChoixAccesRapide;
	public uniteTempsSelectionnee: UniteTemps;

	ngOnInit() {
		// La date de début est réglée à minuit de cette journée
		this.dateDebut.setHours(0, 0, 0, 0);
		// La date de fin est réglée à minuit le jour suivant
		this.dateFin.setHours(24, 0, 0, 0);

		// Récupération des accès rapides avec des temps en minutes
		this.accesRapidesMinutes = this.accesRapides.filter(accesRapide => accesRapide.codeUniteTemps === CodeUniteTemps.MINUTE);
		// Récupération des accès rapide avec des temps en heures
		this.accesRapidesHeures = this.accesRapides.filter(accesRapide => accesRapide.codeUniteTemps === CodeUniteTemps.HEURE);
		// Récupération des accès rapides avec des temps en jours
		this.accesRapidesJours = this.accesRapides.filter(accesRapide => accesRapide.codeUniteTemps === CodeUniteTemps.JOUR);
	}

	/**
	 * Méthode pour ouvrir la section de sélection de dates
	 */
	public ouvrirSelection(): void {
		this.selecteurDateOuvert = true;
	}

	/**
	 * Méthode pour gérer la fermeture de la section de sélection de dates
	 */
	public fermetureSelecteurDate() {
		// Update des dates pour le composant supérieur
		this.emitDate();
		this.selecteurDateOuvert = false;
	}

	/**
	 * Méthode de formatage une date en string pour afficher dans le champ input
	 */
	public formatterDate(): string {
		let dateFormatee = '';

		if (this.dateDebut !== null) {
			dateFormatee = this.dateDebut.toLocaleDateString('fr-FR', {hour: '2-digit', minute: '2-digit', second: '2-digit'}) + ' - ';
		}

		if (this.dateDebut !== null && this.dateFin !== null) {
			dateFormatee = dateFormatee + this.dateFin.toLocaleDateString('fr-FR', {hour: '2-digit', minute: '2-digit', second: '2-digit'});
		}

		return dateFormatee;
	}

	/**
	 * Méthode permettant de calculer une date absolue à partie d'une période de temps relative ou d'un accès rapide
	 *
	 * @param accesRapide un booléen indiquant si l'utilisateur utilise un accès rapide pour définir la période de dates
	 * @param calculIncrementDecrementDate booléen indiquant si l'on veut calculer un incrément ou un décrément sur la période de dates
	 * @param incrementDate un booléen indiquant si on souhaite incrémenter ou décrémenter la période de date, utilisée par les boutons de navigation dans le temps
	 */
	public calculerDate(accesRapide = false, calculIncrementDecrementDate = false, incrementDate = false): void {
		// Vérification si la durée rentrée par l'utilisateur est positive ou si on veut calculer une période à partir d'un accès rapide
		if ((!accesRapide && this.duree > 0 && !calculIncrementDecrementDate) || (accesRapide && this.accesRapideSelectionne.codeUniteTemps !== CodeUniteTemps.JOUR)) {
			let tempsEnMillis: number;

			// Si on traite un accès rapide, le calcul est différent
			if (accesRapide) {
				// Remise à zero de la durée relative
				this.duree = undefined;

				// Récupération de la valeur de l'unité de temps en millisecondes
				const valeurUniteMillisecondes = this.unitesTemps.find(unite => unite.code === this.accesRapideSelectionne.codeUniteTemps).tempsMillis;

				// Calcul du temps en millisecondes à soustraire à la date actuelle
				tempsEnMillis = this.accesRapideSelectionne.valeurTemps * valeurUniteMillisecondes;

				// Fermeture du sélecteur de date une fois le choix effectué par l'utilisateur
				this.selecteurDateOuvert = false;
			}
			else {
				// Remise à zero de l'ancien accès rapide sélectionné
				this.accesRapideSelectionne = undefined;

				// Calcul du temps en millisecondes à soustraire à la date actuelle
				tempsEnMillis = this.duree * this.uniteTempsSelectionnee.tempsMillis;
			}

			// Nouvelle date de fin sur l'heure actuelle
			this.dateFin = new Date();

			// Soustraction de la différence de temps (en ms) à la date de fin (actuelle) pour avoir la date de début
			this.dateDebut = new Date(this.dateFin.getTime() - tempsEnMillis);

			// Emit des nouvelles dates pour mettre à jour les filtres
			this.emitDate();
		}
		// Si l'accés rapide sélectionné est en jour, le calcul des dates est différent
		else if (accesRapide && this.accesRapideSelectionne.codeUniteTemps === CodeUniteTemps.JOUR) {
			// Remise à zero de la durée relative
			this.duree = undefined;

			// Cas spécial si l'utilisateur a choisi aujourd'hui
			if (this.accesRapideSelectionne.id === 'AUJO') {
				this.reinitialiserDate();
			}
			else {
				this.dateDebut = new Date();
				this.dateFin = new Date();
				this.dateDebut.setHours(-24 * this.accesRapideSelectionne.valeurTemps, 0, 0, 0);
				this.dateFin.setHours(0, 0, 0, 0);
			}

			// Fermeture du sélecteur de date une fois le choix effectué par l'utilisateur
			this.selecteurDateOuvert = false;
		}
		// On souhaite calculer un incrément ou un décrément sur la période avec les boutons
		else if (!accesRapide && calculIncrementDecrementDate) {
			// Calcul de la durée en millisecondes de la période actuelle
			const deltaPeriodeMs = this.dateFin.getTime() - this.dateDebut.getTime();

			// On souhaite incrémenter la période avec le delta actuel entre les dates
			if (incrementDate) {
				this.dateDebut = new Date(this.dateFin.getTime());
				this.dateFin = new Date(this.dateDebut.getTime() + deltaPeriodeMs);
			}
			// On souhaite décrémenter la période avec le delta actuel entre les dates
			else {
				this.dateFin = new Date(this.dateDebut.getTime());
				this.dateDebut = new Date(this.dateFin.getTime() - deltaPeriodeMs);
			}

			// Si on était dans le cas d'un accès rapide sélectionné, on ne l'est plus, car la période ne correspond plus
			this.accesRapideSelectionne = undefined;
			// Idem si l'utilisateur avait sélectionné une durée relative
			this.duree = undefined;

			this.emitDate();
		}
	}

	/**
	 * Méthode pour remettre à 0 la période de date
	 */
	public reinitialiserDate(): void {
		// La période est remise sur la journée actuelle
		this.dateDebut = new Date();
		this.dateFin = new Date();
		this.dateDebut.setHours(0, 0, 0, 0);
		this.dateFin.setHours(24, 0, 0, 0);

		// Remise à zero de l'accès rapide sélectionné
		this.accesRapideSelectionne = undefined;

		// Remise à zero de la durée relative
		this.duree = undefined;
	}

	/**
	 * Méthode de vérification que la date de début est bien avant celle de fin
	 */
	public verificationOrdreDatesIncorrect(): boolean {
		return this.dateDebut > this.dateFin;
	}

	/**
	 * Méthode permettant de gérer la sélection d'une date de type absolu
	 */
	protected gererSelectionDateAbsolue() {
		// Remise à zéro de la durée relative
		this.duree = undefined;
		// Remise à zéro de l'accès rapide sélectionné
		this.accesRapideSelectionne = undefined;

		// Ajout d'un contrôle pour toujours avoir une période de dates valide
		// Si pas de date de début ou qu'elle est mal saisie
		if (this.dateDebut === null) {
			this.dateDebut = new Date();
			this.dateDebut.setHours(0, 0, 0, 0);
		}

		// Si on a une date de début et pas de date de fin ou qu'elle est mal saisie
		if (this.dateDebut !== null && this.dateFin === null) {
			this.dateFin = new Date();
			this.dateFin.setHours(24, 0, 0, 0);
		}

		// Emit des nouvelles dates vers les filtres
		this.emitDate();
	}

	/**
	 * Méthode permettant d'émettre les nouvelles dates sélectionnées
	 */
	public emitDate() {
		this.dateDebutChange.emit(this.dateDebut);
		this.dateFinChange.emit(this.dateFin);
	}

	traductionFR(): any {
		return {
			'SELECTEUR_DATE': {
				'TITRE_ACCES_RAPIDE': 'Accès rapide',
				'TITRE_DATE_RELATIVE': 'Date relative',
				'TITRE_DATE_ABSOLUE': 'Date absolue',
				'LABEL_DATE_DEBUT': 'Date début',
				'LABEL_DATE_FIN': 'Date fin',
				'LABEL_DUREE': 'Durée',
				'LABEL_UNITE_TEMPS': 'Unité de temps',
				'LABEL_UNITE_SECONDES': 'Secondes',
				'LABEL_UNITE_MINUTES': 'Minutes',
				'LABEL_UNITE_HEURES': 'Heures',
				'LABEL_UNITE_JOURS': 'Jours',
				'LABEL_BOUTON_REINITIALISER_DATE': 'Réinitialiser',
				'LABEL_BOUTON_VALIDER': 'Valider',
				'ACCES_RAPIDE': {
					'LABEL_1_MINUTE': 'Dernière minute',
					'LABEL_15_MINUTES': '15 dernières minutes',
					'LABEL_30_MINUTES': '30 dernières minutes',
					'LABEL_45_MINUTES': '45 dernières minutes',
					'LABEL_1_HEURE': 'Dernière heure',
					'LABEL_2_HEURES': '2 dernières heures',
					'LABEL_4_HEURES': '4 dernières heures',
					'LABEL_12_HEURES': '12 dernières heures',
					'LABEL_24_HEURES': '24 dernières heures',
					'LABEL_48_HEURES': '48 dernières heures',
					'LABEL_AUJOURDHUI': 'Aujourd\'hui',
					'LABEL_HIER': 'Hier',
					'LABEL_7_DERNIERS_JOURS': '7 derniers jours'
				}
			}
		};
	}

	traductionIT(): any {
		return {
			'SELECTEUR_DATE': {
				'TITRE_ACCES_RAPIDE': 'Accès rapide',
				'TITRE_DATE_RELATIVE': 'Date relative',
				'TITRE_DATE_ABSOLUE': 'Date absolue',
				'LABEL_DATE_DEBUT': 'Date début',
				'LABEL_DATE_FIN': 'Date fin',
				'LABEL_DUREE': 'Durée',
				'LABEL_UNITE_TEMPS': 'Unité de temps',
				'LABEL_UNITE_SECONDES': 'Secondes',
				'LABEL_UNITE_MINUTES': 'Minutes',
				'LABEL_UNITE_HEURES': 'Heures',
				'LABEL_UNITE_JOURS': 'Jours',
				'LABEL_BOUTON_REINITIALISER_DATE': 'Réinitialiser',
				'LABEL_BOUTON_VALIDER': 'Valider',
				'ACCES_RAPIDE': {
					'LABEL_1_MINUTE': 'Dernière minute',
					'LABEL_15_MINUTES': '15 dernières minutes',
					'LABEL_30_MINUTES': '30 dernières minutes',
					'LABEL_45_MINUTES': '45 dernières minutes',
					'LABEL_1_HEURE': 'Dernière heure',
					'LABEL_2_HEURES': '2 dernières heures',
					'LABEL_4_HEURES': '4 dernières heures',
					'LABEL_12_HEURES': '12 dernières heures',
					'LABEL_24_HEURES': '24 dernières heures',
					'LABEL_48_HEURES': '48 dernières heures',
					'LABEL_AUJOURDHUI': 'Aujourd\'hui',
					'LABEL_HIER': 'Hier',
					'LABEL_7_DERNIERS_JOURS': '7 derniers jours'
				}
			}
		};
	}
}
